import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, App, Button, Card, Col, DatePicker, Form, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, EyeOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import recognitionService, { POSTING_GRANULARITIES, type RunFilters, type RunPreview } from '@/services/financeRecognition.service';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import { useProjectsForSelection } from '@/hooks/useProject';
import { accountLabel, amount, apiErrorMessage, contractPath, humanize, options, useRecognitionAccess } from './prepaidDisplay';

const { Title, Text } = Typography;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

const PrepaidRunNewPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const access = useRecognitionAccess();
  const [form] = Form.useForm();
  const [preview, setPreview] = useState<RunPreview | null>(null);

  const { data: categories = [] } = useQuery({ queryKey: ['recognition', 'categories'], queryFn: () => recognitionService.listCategories() });
  const { data: policy } = useQuery({ queryKey: ['recognition', 'policy'], queryFn: () => recognitionService.getPolicy() });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-list-prepaid'],
    queryFn: async () => unwrap<any[]>(await api.get('/procurement/suppliers')) ?? [],
  });
  const { data: expenseAccounts = [] } = useChartAccountsByType('Expense', false);
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);
  const { data: projects = [] } = useProjectsForSelection();

  const filters = (): RunFilters => {
    const v = form.getFieldsValue();
    return {
      through_date: dayjs(v.through_date).format('YYYY-MM-DD'),
      category_id: v.category_id ?? null,
      supplier_id: v.supplier_id ?? null,
      expense_account_id: v.expense_account_id ?? null,
      prepaid_asset_account_id: v.prepaid_asset_account_id ?? null,
      project_id: v.project_id ?? null,
    };
  };

  const previewMutation = useMutation({
    mutationFn: () => recognitionService.previewRun(filters()),
    onSuccess: setPreview,
    onError: (e) => message.error(apiErrorMessage(e, 'Preview failed')),
  });

  const createMutation = useMutation({
    mutationFn: async (process: boolean) => {
      const v = form.getFieldsValue();
      const run = await recognitionService.createRun({ ...filters(), posting_action: v.posting_action, posting_granularity: v.posting_granularity ?? null });
      return process ? recognitionService.processRun(run.id) : run;
    },
    onSuccess: (run) => {
      message.success(`Run ${run.run_number}: ${humanize(run.status).toLowerCase()}`);
      queryClient.invalidateQueries({ queryKey: ['recognition'] });
      navigate(`/fin/prepaid/runs/${run.id}`);
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Run failed')),
  });

  if (!access.canRun) return <Alert type="warning" showIcon style={{ margin: 24 }} message={t('needPermissionCreateRuns')} />;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/prepaid/runs')}>
          {t('runs')}
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {t('newRecognitionRun')}
        </Title>
      </Space>

      <Form form={form} layout="vertical" initialValues={{ through_date: dayjs().endOf('month'), posting_action: 'POST' }} onValuesChange={() => setPreview(null)}>
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} md={4}>
              <Form.Item name="through_date" label={t('recogniseThrough')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="category_id" label={t('category')}>
                <Select allowClear options={categories.map((c) => ({ value: c.id, label: c.name }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="supplier_id" label={t('supplier')}>
                <Select allowClear showSearch optionFilterProp="label" options={suppliers.map((s: any) => ({ value: s.id, label: s.supplier_name }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="expense_account_id" label={t('expenseAccount')}>
                <Select allowClear showSearch optionFilterProp="label" options={expenseAccounts.map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="prepaid_asset_account_id" label={t('prepaidAccount')}>
                <Select allowClear showSearch optionFilterProp="label" options={assetAccounts.map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="project_id" label={t('project')}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={(projects as any[]).map((p) => ({ value: p.id, label: p.project_number ? `${p.project_number} - ${p.project_name}` : p.project_name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="posting_action" label={t('onProcessing')}>
                <Select
                  options={[
                    { value: 'POST', label: t('postJournals') },
                    { value: 'DRAFT', label: t('createDraftJournalsForApproval') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="posting_granularity" label={t('granularity')} extra={policy ? `Blank = policy (${humanize(policy.posting_granularity)})` : undefined}>
                <Select allowClear options={options(POSTING_GRANULARITIES)} />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button icon={<EyeOutlined />} loading={previewMutation.isPending} onClick={() => form.validateFields().then(() => previewMutation.mutate())}>
              {t('preview')}
            </Button>
            <Button disabled={!preview || preview.readyCount === 0} loading={createMutation.isPending && createMutation.variables === false} onClick={() => createMutation.mutate(false)}>
              {t('saveRunValidateOnly')}
            </Button>
            {access.canPost && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                disabled={!preview || preview.readyCount === 0}
                loading={createMutation.isPending && createMutation.variables === true}
                onClick={() => createMutation.mutate(true)}
              >
                {t('createAndProcess')}
              </Button>
            )}
          </Space>
        </Card>
      </Form>

      {preview && (
        <Card title={`Preview through ${preview.throughDate}`}>
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col xs={12} md={6}>
              <Statistic title={t('lines')} value={preview.itemCount} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t('eligibleBase')} value={amount(preview.eligibleAmount)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={`Ready (${preview.readyCount})`} value={amount(preview.readyAmount)} valueStyle={{ color: '#389e0d' }} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={`Exceptions (${preview.exceptionCount})`} value={amount(preview.exceptionAmount)} valueStyle={{ color: preview.exceptionCount ? '#cf1322' : undefined }} />
            </Col>
          </Row>
          {preview.truncated && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('previewTruncatedHint')} />}
          <Table
            size="small"
            rowKey="lineId"
            dataSource={preview.items}
            pagination={{ pageSize: 50 }}
            columns={[
              { title: t('contract'), dataIndex: 'recognitionNumber', render: (v: string, r) => <Link to={contractPath(r.contractId)}>{v}</Link> },
              { title: t('description'), dataIndex: 'description', ellipsis: true },
              { title: t('supplier'), dataIndex: 'supplierName', ellipsis: true },
              { title: t('period'), dataIndex: 'periodKey', width: 90 },
              { title: t('postingDate'), dataIndex: 'postingDate', width: 110 },
              { title: t('amountBase'), dataIndex: 'baseAmount', width: 140, align: 'right', render: (v: string) => amount(v) },
              {
                title: tc('status'),
                key: 'st',
                width: 260,
                render: (_: unknown, r) =>
                  r.status === 'READY' ? (
                    <Tag color="blue">{t('ready')}</Tag>
                  ) : (
                    <Space direction="vertical" size={0}>
                      <Tag color="red">{humanize(r.exceptionCode)}</Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {r.exceptionMessage}
                      </Text>
                    </Space>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

export default PrepaidRunNewPage;
